// StepDetails - VibeTags, Moods, Activities, Community, Bilder, Kontakt, Valou-Bereiche, showSubmitter
// Redesigned with Accordion sections for better organization

import React, { useState, useRef } from 'react';
import { Globe, Phone, Mail, Clock, X, Loader2, Plus, ChevronDown, Image, Sparkles, Users, Hash, UserCircle, MessageCircle, DoorOpen, Hammer, History, Ear, Coffee, Lightbulb, Camera, UserCheck, Layers } from 'lucide-react';
import type { MoodLabel, ActivityLabel, CommunityFeature, OpeningHours, VibeTag, ValouAreaId } from '../../../types';
import { VIBE_TAGS, VALOU_AREAS } from '../../../types';
import { MOODS, ACTIVITIES, COMMUNITY_FEATURES } from '../../../constants';
import { uploadPlaceImage, validateImageFile, compressImage } from '../../../services/storageService';

// Icon mapping for VibeTags
const VIBE_ICON_MAP: Record<VibeTag, React.ReactNode> = {
  rollenlos: <UserCircle className="w-4 h-4" />,
  schnack_potential: <MessageCircle className="w-4 h-4" />,
  unverfuegbares_glueck: <Sparkles className="w-4 h-4" />,
  offen_fuer_alle: <DoorOpen className="w-4 h-4" />,
  selbstwirksam: <Hammer className="w-4 h-4" />,
  antiquiert_gut: <History className="w-4 h-4" />,
  empfangsbereit: <Ear className="w-4 h-4" />,
  anlasslos: <Coffee className="w-4 h-4" />,
  selbstueberraschung: <Lightbulb className="w-4 h-4" />,
};

const MAX_IMAGES = 5;

// Accordion Section Component
interface AccordionSectionProps {
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  badge?: string;
  required?: boolean;
}

function AccordionSection({ title, icon, isOpen, onToggle, children, badge, required }: AccordionSectionProps) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
            {icon}
          </div>
          <span className="font-medium text-gray-900">{title}</span>
          {required && <span className="text-red-500 text-sm">*</span>}
          {badge && (
            <span className="px-2 py-0.5 bg-indigo-500/10 text-indigo-600 text-xs font-medium rounded-full">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="p-4 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
}

interface ImagePreview {
  id: string;
  previewUrl: string;
  uploadedUrl?: string;
  isUploading: boolean;
  error?: string;
}

interface FormData {
  images: string[];
  vibeTags: VibeTag[];
  moods: MoodLabel[];
  activities: ActivityLabel[];
  community: CommunityFeature[];
  valouAreas: ValouAreaId[];
  openingHours: OpeningHours;
  website: string;
  phone: string;
  email: string;
  showSubmitter: boolean;
}

interface StepDetailsProps {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
}

export function StepDetails({ formData, updateFormData }: StepDetailsProps) {
  const [imagePreviews, setImagePreviews] = useState<ImagePreview[]>(() =>
    formData.images.map((url, index) => ({
      id: `existing-${index}`,
      previewUrl: url,
      uploadedUrl: url,
      isUploading: false,
    }))
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Accordion state - Character section is open by default
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    vibeTags: true,   // Open by default - GoodPlace-Charakter
    character: true,  // Stimmung & Atmosphäre - auch offen da Pflicht
    images: false,
    valouAreas: false,
    hours: false,
    contact: false,
    submitter: false,
  });

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Reset error state
    setUploadError(null);

    // Check max images limit
    const remainingSlots = MAX_IMAGES - imagePreviews.length;
    if (remainingSlots <= 0) {
      setUploadError(`Maximal ${MAX_IMAGES} Bilder erlaubt`);
      return;
    }

    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    for (const file of filesToProcess) {
      // Validate file
      const validationError = validateImageFile(file);
      if (validationError) {
        setUploadError(validationError);
        continue;
      }

      const previewId = `upload-${Date.now()}-${Math.random()}`;

      // Create preview immediately
      const reader = new FileReader();
      reader.onload = (e) => {
        const previewUrl = e.target?.result as string;

        setImagePreviews(prev => [...prev, {
          id: previewId,
          previewUrl,
          isUploading: true,
        }]);

        // Start upload
        uploadImage(file, previewId);
      };
      reader.readAsDataURL(file);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async (file: File, previewId: string) => {
    try {
      const compressedFile = await compressImage(file);
      const result = await uploadPlaceImage(compressedFile);

      setImagePreviews(prev => prev.map(img =>
        img.id === previewId
          ? { ...img, uploadedUrl: result.url, isUploading: false }
          : img
      ));

      // Update formData with all successfully uploaded images
      setImagePreviews(prev => {
        const uploadedUrls = prev
          .filter(img => img.uploadedUrl && !img.isUploading)
          .map(img => img.uploadedUrl!);
        updateFormData({ images: uploadedUrls });
        return prev;
      });
    } catch (error) {
      console.error('Image upload failed:', error);
      setImagePreviews(prev => prev.map(img =>
        img.id === previewId
          ? { ...img, isUploading: false, error: 'Upload fehlgeschlagen' }
          : img
      ));
    }
  };

  const handleRemoveImage = (previewId: string) => {
    setImagePreviews(prev => {
      const newPreviews = prev.filter(img => img.id !== previewId);
      const uploadedUrls = newPreviews
        .filter(img => img.uploadedUrl && !img.isUploading)
        .map(img => img.uploadedUrl!);
      updateFormData({ images: uploadedUrls });
      return newPreviews;
    });
  };

  const canAddMore = imagePreviews.length < MAX_IMAGES;

  const toggleVibeTag = (tag: VibeTag) => {
    const newVibeTags = formData.vibeTags.includes(tag)
      ? formData.vibeTags.filter((t) => t !== tag)
      : [...formData.vibeTags, tag];
    updateFormData({ vibeTags: newVibeTags });
  };

  const toggleMood = (mood: MoodLabel) => {
    const newMoods = formData.moods.includes(mood)
      ? formData.moods.filter((m) => m !== mood)
      : [...formData.moods, mood];
    updateFormData({ moods: newMoods });
  };

  const toggleActivity = (activity: ActivityLabel) => {
    const newActivities = formData.activities.includes(activity)
      ? formData.activities.filter((a) => a !== activity)
      : [...formData.activities, activity];
    updateFormData({ activities: newActivities });
  };

  const toggleCommunity = (feature: CommunityFeature) => {
    const newCommunity = formData.community.includes(feature)
      ? formData.community.filter((c) => c !== feature)
      : [...formData.community, feature];
    updateFormData({ community: newCommunity });
  };

  const toggleValouArea = (areaId: ValouAreaId) => {
    const newValouAreas = formData.valouAreas.includes(areaId)
      ? formData.valouAreas.filter((a) => a !== areaId)
      : [...formData.valouAreas, areaId];
    updateFormData({ valouAreas: newValouAreas });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Details zum Ort
        </h2>
        <p className="text-gray-600">
          Beschreibe die Atmosphäre und was man hier erleben kann.
        </p>
      </div>

      {/* Hidden file inputs for images */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        multiple
        className="hidden"
      />
      {/* Camera input for mobile */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Accordion Sections */}
      <div className="space-y-3">
        {/* GoodPlace Vibe Tags Section */}
        <AccordionSection
          title="GoodPlace-Charakter"
          icon={<Hash className="w-4 h-4 text-green-600" />}
          isOpen={openSections.vibeTags}
          onToggle={() => toggleSection('vibeTags')}
          badge={formData.vibeTags.length > 0 ? `${formData.vibeTags.length} gewählt` : undefined}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Was macht diesen Ort zu einem <strong>guten Ort</strong>? Wähle passende Tags aus.
            </p>
            <div className="flex flex-wrap gap-2">
              {VIBE_TAGS.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleVibeTag(tag.id)}
                  title={tag.description}
                  className={`group relative px-4 py-2.5 rounded-xl border-2 transition text-sm flex items-center gap-2 ${
                    formData.vibeTags.includes(tag.id)
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-700 hover:border-green-300 hover:bg-green-50/50'
                  }`}
                >
                  {VIBE_ICON_MAP[tag.id]}
                  <span>{tag.label}</span>
                  {/* Tooltip on hover */}
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap max-w-[200px] text-center z-10">
                    {tag.description}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 italic">
              Tipp: Fahre mit der Maus über einen Tag, um die Beschreibung zu sehen.
            </p>
          </div>
        </AccordionSection>

        {/* Character & Mood Section */}
        <AccordionSection
          title="Stimmung & Aktivitäten"
          icon={<Sparkles className="w-4 h-4 text-indigo-600" />}
          isOpen={openSections.character}
          onToggle={() => toggleSection('character')}
          required
          badge={formData.moods.length > 0 ? `${formData.moods.length} gewählt` : undefined}
        >
          <div className="space-y-6">
            {/* Moods */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Stimmung & Atmosphäre <span className="text-red-500">*</span>
                <span className="font-normal text-gray-500 ml-1">(mindestens eine)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {MOODS.map((mood) => (
                  <button
                    key={mood.label}
                    type="button"
                    onClick={() => toggleMood(mood.label)}
                    className={`px-4 py-2 rounded-full border-2 transition text-sm ${
                      formData.moods.includes(mood.label)
                        ? 'border-indigo-500 bg-indigo-500/10 text-indigo-600'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {mood.icon} {mood.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Activities */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Was kann man hier machen?
              </label>
              <div className="flex flex-wrap gap-2">
                {ACTIVITIES.map((activity) => (
                  <button
                    key={activity.label}
                    type="button"
                    onClick={() => toggleActivity(activity.label)}
                    className={`px-4 py-2 rounded-full border-2 transition text-sm ${
                      formData.activities.includes(activity.label)
                        ? 'border-secondary bg-secondary/10 text-secondary'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {activity.icon} {activity.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Community Features */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Gemeinschafts-Features
              </label>
              <div className="flex flex-wrap gap-2">
                {COMMUNITY_FEATURES.map((feature) => (
                  <button
                    key={feature.label}
                    type="button"
                    onClick={() => toggleCommunity(feature.label)}
                    className={`px-4 py-2 rounded-full border-2 transition text-sm ${
                      formData.community.includes(feature.label)
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {feature.icon} {feature.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </AccordionSection>

        {/* Images Section */}
        <AccordionSection
          title="Bilder"
          icon={<Image className="w-4 h-4 text-blue-500" />}
          isOpen={openSections.images}
          onToggle={() => toggleSection('images')}
          badge={imagePreviews.length > 0 ? `${imagePreviews.length}/${MAX_IMAGES}` : undefined}
        >
          <div className="space-y-4">
            {/* Upload Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canAddMore}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-indigo-500 hover:bg-indigo-500/5 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-5 h-5 text-gray-500" />
                <span className="text-gray-700">Bilder auswählen</span>
              </button>
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={!canAddMore}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-50 border-2 border-indigo-200 rounded-xl hover:bg-indigo-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Camera className="w-5 h-5 text-indigo-600" />
                <span className="text-indigo-700">Foto aufnehmen</span>
              </button>
            </div>

            {/* Image Grid */}
            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {imagePreviews.map((img, index) => (
                  <div
                    key={img.id}
                    className="relative aspect-[4/3] rounded-xl overflow-hidden border border-gray-200 bg-gray-50"
                  >
                    <img
                      src={img.previewUrl}
                      alt={`Bild ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {index === 0 && (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-indigo-500 text-white text-xs font-medium rounded-full">
                        Titelbild
                      </div>
                    )}
                    {img.isUploading && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="flex flex-col items-center gap-1 text-white">
                          <Loader2 className="w-6 h-6 animate-spin" />
                          <span className="text-xs">Hochladen...</span>
                        </div>
                      </div>
                    )}
                    {img.error && (
                      <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center">
                        <div className="flex flex-col items-center gap-1 text-white text-center p-2">
                          <X className="w-5 h-5" />
                          <span className="text-xs">{img.error}</span>
                        </div>
                      </div>
                    )}
                    {!img.isUploading && (
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(img.id)}
                        className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100 transition"
                      >
                        <X className="w-4 h-4 text-gray-600" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Legal Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Wichtig:</strong> Mit dem Upload bestätige ich, dass ich die Bildrechte
                besitze und die Bilder auf der Plattform öffentlich angezeigt werden dürfen.
              </p>
            </div>

            {/* Technical Requirements */}
            <p className="text-xs text-gray-500">
              Erlaubte Formate: JPG, PNG, WebP (max. 5MB pro Bild). Empfohlene Auflösung: mindestens 800x600 Pixel.
              Das erste Bild wird als Titelbild verwendet.
            </p>

            {uploadError && (
              <p className="text-sm text-red-600">{uploadError}</p>
            )}
          </div>
        </AccordionSection>

        {/* Valou-Bereiche Section - NEW */}
        <AccordionSection
          title="Lebensbereiche (Valou)"
          icon={<Layers className="w-4 h-4 text-purple-500" />}
          isOpen={openSections.valouAreas}
          onToggle={() => toggleSection('valouAreas')}
          badge={formData.valouAreas.length > 0 ? `${formData.valouAreas.length} gewählt` : 'Optional'}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Welche Lebensbereiche werden an diesem Ort angesprochen oder gefördert?
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {VALOU_AREAS.map((area) => (
                <button
                  key={area.id}
                  type="button"
                  onClick={() => toggleValouArea(area.id)}
                  className={`p-3 rounded-xl border-2 transition text-left ${
                    formData.valouAreas.includes(area.id)
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 text-gray-700 hover:border-purple-300'
                  }`}
                >
                  <span className="text-lg mr-2">{area.icon}</span>
                  <span className="text-sm font-medium">{area.shortName}</span>
                </button>
              ))}
            </div>
          </div>
        </AccordionSection>

        {/* Opening Hours Section */}
        <AccordionSection
          title="Öffnungszeiten"
          icon={<Clock className="w-4 h-4 text-amber-500" />}
          isOpen={openSections.hours}
          onToggle={() => toggleSection('hours')}
          badge={formData.openingHours.displayText ? 'Ausgefüllt' : undefined}
        >
          <div className="space-y-3">
            <div className="relative">
              <Clock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.openingHours.displayText}
                onChange={(e) =>
                  updateFormData({
                    openingHours: { ...formData.openingHours, displayText: e.target.value },
                  })
                }
                placeholder="z.B. Mo-Fr 8-18 Uhr, Sa 10-16 Uhr"
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
            <p className="text-xs text-gray-500">
              Gib die Öffnungszeiten als Freitext ein. Beispiel: "Mo-Fr 9-18 Uhr" oder "Immer geöffnet"
            </p>
          </div>
        </AccordionSection>

        {/* Contact Section */}
        <AccordionSection
          title="Kontakt"
          icon={<Users className="w-4 h-4 text-green-500" />}
          isOpen={openSections.contact}
          onToggle={() => toggleSection('contact')}
          badge={formData.website || formData.phone || formData.email ? 'Ausgefüllt' : undefined}
        >
          <div className="space-y-4">
            <div className="relative">
              <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={formData.website}
                onChange={(e) => updateFormData({ website: e.target.value })}
                placeholder="Website (z.B. https://example.com)"
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>

            <div className="relative">
              <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateFormData({ phone: e.target.value })}
                placeholder="Telefon (z.B. 030 12345678)"
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>

            <div className="relative">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateFormData({ email: e.target.value })}
                placeholder="E-Mail (z.B. info@example.com)"
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
          </div>
        </AccordionSection>

        {/* Submitter Display Section - NEW */}
        <AccordionSection
          title="Einreicher anzeigen"
          icon={<UserCheck className="w-4 h-4 text-teal-500" />}
          isOpen={openSections.submitter}
          onToggle={() => toggleSection('submitter')}
          badge={formData.showSubmitter ? 'Aktiviert' : 'Optional'}
        >
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border-2 border-gray-200 hover:border-teal-300 transition">
              <input
                type="checkbox"
                checked={formData.showSubmitter}
                onChange={(e) => updateFormData({ showSubmitter: e.target.checked })}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <div>
                <p className="font-medium text-gray-900">Als Einreicher auf der Place-Seite anzeigen</p>
                <p className="text-sm text-gray-500 mt-1">
                  Dein Name wird auf der Detailseite des GoodPlace angezeigt, damit andere sehen können,
                  wer diesen besonderen Ort entdeckt und vorgeschlagen hat.
                </p>
              </div>
            </label>
          </div>
        </AccordionSection>
      </div>

      {/* Submit Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Was passiert nach dem Einreichen?</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>Dein Vorschlag wird von unserem Team geprüft</li>
          <li>Nach der Freigabe erscheint der Ort in der App</li>
          <li>Du erhältst eine Benachrichtigung über den Status</li>
        </ul>
      </div>
    </div>
  );
}

export default StepDetails;
